/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoading } from 'components/process/Loading';
import PropTypes from 'prop-types';
import MapChart, { PolygonData, SiteImage } from 'components/mapchart/MapChart';
import {
  findSetupOnSiteImage,
  findSetupPrjSummAuth,
  findSetupPrjSummDashboard,
  findSetupPrjSummDelayIssue,
  findSetupPrjSummSampleSchedule,
  findSetupPrjSummTrackRecord,
  updateSetupPrjSummDelayIssue,
} from 'apis/setup/home/SetupProjectSummary';
import {
  SetupProjectSummary,
  SetupProjectSummaryAutority,
  SetupProjectSummaryCondition,
} from 'models/setup/home/SetupProjectSummary';
import { SetupMapChart } from 'models/setup/SetupMapChart';
import { findSetupMapChart, findSetupMapChartList } from 'apis/setup/SetupMapChart';
import CustomGrid from 'components/grids/CustomGrid';
import { SummaryContainer } from 'components/home/setupLayout';
import { ListItem, SummaryItem } from 'components/home/SummaryItem';
import {
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  css,
} from '@mui/material';

import { IconButton } from 'components/buttons/IconSVG';
import { NormalButton } from 'components/buttons/ButtonStyle';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { tabs } from 'components/layouts/Tab';
import { addClass } from '@grapecity/wijmo';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { ComboBox } from 'components/selects/ComboBox';
import { SetupProject } from 'models/setup/SetupProject';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { findProject } from 'apis/setup/SetupProject';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SVGIcon } from 'components/buttons/IconSVG';
import { CommonText } from 'ui/theme/Color';
import CustomDialog from 'components/modals/common/CustomDialog';
import useSessionStore from 'stores/useSessionStore';
import { findScheduleStatus, findScheduleStatusDetail } from 'apis/setup/SetupSchedule';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import SetupEquipmentScheduleChartPopup from 'pages/setup/popup/SetupEquipmentScheduleChartPopup';
import ProjectIssueCreatePage from 'pages/setup/issue/ProjectIssueRegisterPage';
import { PageMode } from 'models/common/Common';
import { CommonUtil } from 'utils/CommonUtil';
import SetupScheduleDelayStatusPopup from '../../popup/SetupScheduleDelayStatusPopup';

/**
 * @description 설비셋업관리 프로젝트별요약
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  26.02.09    jihwan89       프로젝트명 콤보박스 개별 넓이 적용, 프로젝트명 FOB, 공장상세 LAYOUT 파라미터 추가
 *  26.03.09    jshwang12      일정지연보기 버튼 추가
 */

const SetupProjectSummaryPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const navigate = useNavigate();
  const { elmPrjId } = useSessionStore();
  const location = useLocation();
  const state = { ...location.state };

  const recordGridRef = useRef<any>();
  const issueGridRef = useRef<any>();
  const issuePopupGridRef = useRef<any>();
  const [currTab, setCurrTab] = useState<number>(0);
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [condition, setCondition] = useState<SetupProjectSummaryCondition>({
    elmPrjId: state.elmPrjId || elmPrjId || '',
  });
  const [authority, setAuthority] = useState<SetupProjectSummaryAutority[]>([]);
  const [mapChart, setMapChart] = useState<SetupMapChart>({});
  const [mapChartList, setMapChartList] = useState<SetupMapChart[]>([]);
  const [smplSchdList, setSmplSchdList] = useState<SetupProjectSummary[]>([]);
  const [dsbdList, setDsbdList] = useState<SetupProjectSummary[]>([]);
  const [trackRecordList, setTrackRecordList] = useState<SetupProjectSummary[]>([]);
  const [delayIssuList, setDelayIssuList] = useState<SetupProjectSummary[]>([]);

  const [openIssuePopup, setOpenIssuePopup] = useState<boolean>(false);
  const [delayIssuPopupList, setDelayIssuPopupList] = useState<SetupProjectSummary[]>([]);
  const [onSiteImgList, setOnSiteImgList] = useState<SiteImage[]>([]);

  const [isOpenScheduleChart, setOpenScheduleChart] = useState<boolean>(false);
  const [scheduleChartCondition, setScheduleChartCondition] = useState<any>();

  const [isOpenScheduleDelayStatusPopup, setOpenScheduleDelayStatusPopup] =
    useState<boolean>(false);

  const [openIssueDetailPopup, setOpenIssueDetailPopup] = useState<boolean>(false);
  const [registerParam, setRegisterParam] = useState<any>();

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (condition?.bildPlntCd || '').length > 0 ? condition.bildPlntCd === o.bildPlntCd : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, condition?.bildPlntCd]);

  const trackRecordLayout = useMemo(() => {
    return [
      {
        binding: 'prdnProcNm',
        visible: true,
        header: String(t('setup.label.공정', '공정')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'eqpNm',
        visible: true,
        header: String(t('setup.label.설비', '설비')),
        align: 'center',
        width: 180,
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            onClickRecord(ctx.item);
          },
        }),
      },
      {
        binding: 'prjSpNm',
        visible: true,
        header: String(t('setup.label.Step', 'Step')),
        align: 'center',
        width: '*',
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            if (condition.elmPrjId && ctx.item?.tgtEqpId) {
              findScheduleStatusDetail(
                condition.elmPrjId,
                ctx.item?.spTpCd,
                '',
                '',
                ctx.item.tgtEqpId
              ).then((res) => {
                // [26.01.26] 일정현황상세 전체 조회 후 유형별 일정목록
                if (res && ctx.item?.spTpCd) {
                  if (
                    Object.keys(res || {}).filter((key) => key === ctx.item?.spTpCd) &&
                    (res[ctx.item?.spTpCd] || []).length
                  ) {
                    setOpenScheduleChart(true);
                    setScheduleChartCondition({
                      type: 'DTAL',
                      elmPrjId: condition.elmPrjId,
                      tgtEqpId: ctx.item.tgtEqpId,
                      targetData: res[ctx.item?.spTpCd][0],
                      prjSpCd: ctx.item.prjSpCd,
                    });
                  }
                } else {
                  openMessageBar({
                    type: 'warning',
                    content: t(
                      'com.label.조회 가능한 데이터가 없습니다.',
                      '조회 가능한 데이터가 없습니다.'
                    ),
                  });
                }
              });
            }
          },
        }),
      },
      {
        binding: 'planEndDt',
        visible: true,
        header: String(t('setup.label.계획완료일', '계획완료일')),
        align: 'center',
        width: 130,
      },
      {
        binding: 'statNm',
        visible: true,
        header: String(t('setup.label.상태', '상태')),
        align: 'center',
        width: 80,
      },
      {
        binding: 'atulEndDt',
        visible: true,
        header: String(t('setup.label.완료일', '완료일')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'progRt',
        visible: true,
        header: String(t('setup.label.진행률(%)', '진행률(%)')),
        align: 'center',
        width: 100,
      },
    ];
  }, [trackRecordList]);

  const delayIssuLayout = useMemo(() => {
    return [
      {
        binding: 'isuTpNm',
        visible: true,
        header: String(t('setup.label.구분', '구분')),
        align: 'center',
        width: 80,
      },
      {
        binding: 'prdnProcNm',
        visible: true,
        header: String(t('setup.label.공정', '공정')),
        align: 'center',
        width: 80,
      },
      {
        binding: 'eqpNm',
        visible: true,
        header: String(t('setup.label.설비', '설비')),
        align: 'center',
        width: 160,
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            if (condition.elmPrjId && ctx.item?.isuId) {
              setOpenIssueDetailPopup(true);
              setRegisterParam({
                elmPrjId: condition.elmPrjId,
                isuId: ctx.item?.isuId,
              });
            } else {
              openMessageBar({
                type: 'warning',
                content: t(
                  'com.label.조회 가능한 데이터가 없습니다.',
                  '조회 가능한 데이터가 없습니다.'
                ),
              });
            }
          },
        }),
      },
      {
        binding: 'prjSpNm',
        visible: true,
        header: String(t('setup.label.단계', '단계')),
        align: 'center',
        width: 120,
      },
      {
        binding: 'isuTitNm',
        visible: true,
        header: String(t('setup.label.내용', '내용')),
        align: 'center',
        width: '*',
      },
    ];
  }, [delayIssuList]);

  const delayIssuPopupLayout = useMemo(() => {
    return [
      {
        binding: 'bmkDispYn',
        header: String(t('setup.label.표시여부', '표시여부')),
        align: 'center',
        width: 80,
        cssClass: 'WijmoBookmark',
        cellTemplate: (ctx) =>
          `<button class="${ctx.value === 'Y' ? 'detail' : ''}" id="bmkBtn"></button>`,
      },
      {
        binding: 'isuTpNm',
        header: String(t('setup.label.구분', '구분')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'prdnProcNm',
        header: String(t('setup.label.공정', '공정')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'eqpNm',
        header: String(t('setup.label.설비', '설비')),
        align: 'center',
        width: '*',
      },
      {
        binding: 'prjSpNm',
        header: String(t('setup.label.단계', '단계')),
        align: 'center',
        width: 140,
      },
      {
        binding: 'isuTitNm',
        header: String(t('setup.label.내용', '내용')),
        align: 'center',
        width: '*',
      },
    ];
  }, [delayIssuPopupList]);

  const groupedSmplList = useMemo(() => {
    return _.groupBy(smplSchdList, 'prdnProcNm');
  }, [smplSchdList]);

  const badgeList = useMemo(() => {
    if (currTab === 0) {
      return [
        { code: 'PLAN', color: CommonText.Light, text: String(t('setup.label.시작전', '시작전')) },
        { code: 'DLY', color: CommonText.Error, text: String(t('setup.label.지연', '지연')) },
        { code: 'PROG', color: CommonText.Primary, text: String(t('setup.label.진행', '진행')) },
        { code: 'CPLT', color: CommonText.Confirmed, text: String(t('setup.label.완료', '완료')) },
      ];
    } else {
      return [
        {
          code: 'DTBL_PSBL',
          color: CommonText.Confirmed,
          text: String(t('setup.label.자동물류가능', '자동물류가능')),
        },
        {
          code: 'DTBL_IPSB',
          color: CommonText.Error,
          text: String(t('setup.label.자동물류지연', '자동물류지연')),
        },
      ];
    }
  }, [currTab]);

  const filteredTrackRecordList = useMemo(() => {
    return trackRecordList.filter((o) =>
      mapChartList.some((x) => x.selected && x.tgtEqpId === o.tgtEqpId)
    );
  }, [trackRecordList, mapChartList]);

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const a11yCustomProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  const handleChange = (event: any, newValue: React.SetStateAction<number>) => {
    setCurrTab(newValue);
  };

  const onClickPolygon = (data) => {
    const elmPrjId = condition.elmPrjId;
    const tgtEqpId = data.tgtEqpId;

    // 현장 이미지 조회
    const _params = {
      elmPrjId,
      tgtEqpId,
    };
    getOnSiteImgList(_params);
  };

  const fnMoveDetail = (idx) => {
    if (condition.elmPrjId) {
      if (idx === 2) {
        const searchPageCondition = {
          upprMnuUrl: location.pathname,
          mnuNm: t('setup.label.주간 진행률', '주간 진행률'),
          elmPrjId: condition.elmPrjId,
          elmPrjNm: condition.elmPrjNm,
        };
        navigate(`/setup/home/project-graph`, {
          state: searchPageCondition,
        });
      } else {
        const searchPageCondition = {
          upprMnuUrl: location.pathname,
          mnuNm: t('setup.label.프로젝트별 요약 상세', '프로젝트별 요약 상세'),
          elmPrjId: condition.elmPrjId,
          elmPrjNm: condition.elmPrjNm,
          currTab: idx,
        };
        navigate(`/setup/home/project-summary-detail`, {
          state: searchPageCondition,
        });
      }
    } else {
      openMessageBar({
        type: 'error',
        content: t('com.msg.프로젝트를 선택해 주세요.', '프로젝트를 선택해 주세요.'),
      });
    }
  };

  const getOnSiteImgList = (param) => {
    findSetupOnSiteImage(param).then((res) => setOnSiteImgList(res?.map((o) => ({ ...o })) || []));
  };

  const getDsbdClass = (item: SetupProjectSummary, col = 0) => {
    const result: string[] = [];

    if (item.procNm === 'Total') result.push('yellowBg');

    if (
      !mapChartList.every((o) => o.selected) &&
      mapChartList.some(
        (o) =>
          o.selected &&
          o.prdnProcNm === item.procNm &&
          (col === 1 ||
            (o.progStatCd !== 'CPLT' && col === 2) ||
            (o.progStatCd === 'CPLT' && col === 3))
      )
    )
      result.push('greenBg');

    return result.join(' ');
  };

  const onInitialized = useCallback((grid, gbn) => {
    if (gbn === 'RECORD') {
      recordGridRef.current = grid;
    } else {
      issueGridRef.current = grid;
    }
  }, []);

  const btnExcelExport = (gbn = '') => {
    let ref = null;
    let title = '';
    let sheetName = '';

    if (gbn === 'ISSUE') {
      ref = issueGridRef.current;
      title = mapChart.elmPrjNm + ' ' + t('setup.label.실적 List', '실적 List');
      sheetName = t('setup.label.실적 List', '실적 List');
    } else {
      ref = recordGridRef.current;
      title = mapChart.elmPrjNm + ' ' + t('setup.label.이슈', '이슈');
      sheetName = t('setup.label.이슈', '이슈');
    }

    CommonUtil.exportWijmoExcelGrid(ref, title, sheetName);
  };

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('FA_SU_PLNT_CD');
    setBildPlntCode(bildPlntCode);

    Promise.all([findProject({ searchVisibleSaveYn: 'N' })]).then(([result]) => {
      if (result !== null) {
        setPrjMasterData(result);
        if (!state?.elmPrjId && !elmPrjId) {
          setCondition((prev) => ({
            ...prev,
            elmPrjId: result[0]?.elmPrjId || '',
            elmPrjNm: result[0]?.elmPrjNm || '',
          }));
        }
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.F',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const onClickRecord = (item: any) => {
    setMapChartList((prev) => prev.map((o) => ({ ...o, selected: item.tgtEqpId === o.tgtEqpId })));
  };

  useEffect(() => {
    setMapChartList((prev) => prev.map((o) => ({ ...o, selected: true })));
  }, [currTab]);

  useEffect(() => {
    initCodes();
  }, []);

  useEffect(() => {
    // params로 넘어온 프로젝트 ID 값 세팅
    if (state?.elmPrjId && location.pathname === '/setup/home/project-summary') {
      setCondition((prev) => ({ ...prev, elmPrjId: state.elmPrjId }));
    }
  }, [location]);

  useEffect(() => {
    if (openIssuePopup) {
      findSetupPrjSummDelayIssue({ ...condition, bmkDispYn: '' }).then((ISSUE) => {
        setDelayIssuPopupList(ISSUE?.map((o) => ({ ...o })) || []);
      });
    } else {
      findSetupPrjSummDelayIssue({ ...condition, bmkDispYn: 'Y' }).then((ISSUE) => {
        setDelayIssuList(ISSUE?.map((o) => ({ ...o })) || []);
      });
    }
  }, [openIssuePopup]);

  useEffect(() => {
    if (condition.elmPrjId) {
      openLoading(true);
      Promise.all([
        findSetupMapChart(condition.elmPrjId),
        findSetupMapChartList({ elmPrjId: condition.elmPrjId }),
        findSetupPrjSummTrackRecord(condition),
        findSetupPrjSummSampleSchedule(condition),
        findSetupPrjSummDashboard(condition),
        findSetupPrjSummDelayIssue({ ...condition, bmkDispYn: 'Y' }),
        findSetupPrjSummAuth({ ...condition }),
      ])
        .then(([MAP, MAP_LIST, TRACK, SAMPLE, DSBD, ISSUE, auth]) => {
          if (MAP) setMapChart({ ...MAP });
          setMapChartList(
            MAP_LIST?.map((o) => ({ ...o, code: o.progStatCd, selected: true })) || []
          );
          setTrackRecordList(TRACK?.map((o) => ({ ...o })) || []);
          setSmplSchdList(SAMPLE?.map((o) => ({ ...o })) || []);
          setDsbdList(DSBD?.map((o) => ({ ...o })) || []);
          setDelayIssuList(ISSUE?.map((o) => ({ ...o })) || []);
          setAuthority(auth?.map((o) => ({ ...o })) || []);
        })
        .finally(() => openLoading(false));
    }
  }, [condition.elmPrjId]);

  return (
    <>
      <SummaryContainer>
        <div className="userMetadata">
          <span>{t('setup.label.플랜트', '플랜트')}</span>
          <ComboBox
            options={bildPlntCode}
            placeholder={String(t('setup.label.전체', '전체'))}
            defaultValue={condition?.bildPlntCd || ''}
            onChange={(value) => {
              handleOnChange('bildPlntCd', value);
              if (!(prjMasterData || []).filter((o) => condition.elmPrjId === o.elmPrjId).length) {
                handleOnChange('elmPrjId', '');
              }
            }}
          />
          <span>{t('setup.label.프로젝트명', '프로젝트명')}</span>
          <ComboBox
            style={{ width: '400px' }}
            placeholder={String(t('setup.label.선택', '선택'))}
            options={optionsPrjMaster}
            defaultValue={condition.elmPrjId}
            onChange={(value) => {
              handleOnChange('elmPrjId', value);
              handleOnChange('elmPrjNm', prjMasterData.find((o) => o.elmPrjId === value)?.elmPrjNm);
            }}
          />
        </div>
        <SummaryItem className="tabContents">
          <div css={tabs.wrap}>
            <Tabs value={currTab} onChange={handleChange} css={tabs.tab}>
              <Tab label={t('setup.label.공장 Layout', '공장 Layout')} {...a11yCustomProps(0)} />
              <Tab label={t('setup.label.자동물류대응', '자동물류대응')} {...a11yCustomProps(1)} />
            </Tabs>
            <div css={tabs.btn}>
              <NormalButton className="noIconBtn minWidth" onClick={() => fnMoveDetail(0)}>
                {t('setup.label.공장 Layout 상세', '공장 Layout 상세')}
              </NormalButton>
              <NormalButton className="noIconBtn minWidth" onClick={() => fnMoveDetail(1)}>
                {t('setup.label.FOB', 'FOB')}
              </NormalButton>
              <NormalButton className="noIconBtn minWidth" onClick={() => fnMoveDetail(2)}>
                {t('setup.label.주간 진행률', '주간 진행률')}
              </NormalButton>
            </div>
          </div>
          <div className="tabpanel-area">
            {mapChartList.length > 0 && mapChart.drwAtchFileId && (
              <MapChart
                polygonData={mapChartList}
                setPolygonData={setMapChartList}
                imageId={mapChart.drwAtchFileId || ''}
                imageHeight={parseInt(mapChart.imgVtcLen || '0')}
                imageWidth={parseInt(mapChart.imgWthLen || '0')}
                showPopup={true}
                badgeList={badgeList}
                badgeKey={currTab === 0 ? 'progStatCd' : 'dtblStatCd'}
                onClickPolygon={(data, e) => onClickPolygon(data)}
                onSiteImgList={onSiteImgList}
              />
            )}
          </div>
        </SummaryItem>
        <SummaryItem className="Schedule">
          <SubTitleGroup className="mb-2">
            <h3>{t('setup.label.샘플일정', '샘플일정')}</h3>
          </SubTitleGroup>
          <div className="listScroll">
            {Object.entries(groupedSmplList).map(([key, list]) => {
              if (list.every((o) => !o.planStDt && !o.planEndDt)) {
                return null;
              } else {
                return (
                  <ListItem key={key}>
                    <h4>[ {key} ]</h4>
                    <ul>
                      {list
                        .filter((item) => item.planStDt && item.planEndDt)
                        .map((item, idx) => {
                          return (
                            <li key={`${key}-${idx}`}>
                              <strong>{item.smplNm}</strong>
                              <span>{`${item.planStDt} ~ ${item.planEndDt}`}</span>
                              <u>{item.dayLeft}</u>
                            </li>
                          );
                        })}
                    </ul>
                  </ListItem>
                );
              }
            })}
          </div>
        </SummaryItem>
        <SummaryItem className="dashboard">
          <SubTitleGroup className="mb-2">
            <h3>{t('setup.label.Dashboard', 'Dashboard')}</h3>
          </SubTitleGroup>
          <div className="tableScroll">
            <TableContainer css={tb.tableCol}>
              <Table>
                <colgroup>
                  <col width={'*'} />
                  <col width={110} />
                  <col width={110} />
                  <col width={110} />
                  <col width={110} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ height: '30px' }}>
                      {t('setup.label.공정', '공정')}
                    </TableCell>
                    <TableCell style={{ height: '30px' }}>
                      {t('setup.label.전체 (대수)', '전체 (대수)')}
                    </TableCell>
                    <TableCell style={{ height: '30px' }}>
                      {t('setup.label.진행중', '진행중')}
                    </TableCell>
                    <TableCell style={{ height: '30px' }}>
                      {t('setup.label.완료', '완료')}
                    </TableCell>
                    <TableCell style={{ height: '30px' }}>
                      {t('setup.label.진행률', '진행률')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dsbdList.map((item, idx) => {
                    return (
                      <TableRow key={idx}>
                        <TableCell className={getDsbdClass(item)}>{item.procNm}</TableCell>
                        <TableCell className={getDsbdClass(item, 1)}>{item.allCnt}</TableCell>
                        <TableCell className={getDsbdClass(item, 2)}>{item.eqpProcCnt}</TableCell>
                        <TableCell className={getDsbdClass(item, 3)}>{item.eqpCpltCnt}</TableCell>
                        <TableCell className={getDsbdClass(item)}>
                          {Number(item.progRt).toFixed(2)}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </SummaryItem>
        <SummaryItem>
          <div className="gridHeight">
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('setup.label.실적 List', '실적 List')}</h3>
                <span className="total">
                  {t('com.label.총', '총')}
                  <span>{(filteredTrackRecordList || []).length.toLocaleString()}</span>
                  {t('com.label.건', '건')}
                </span>
              </SubTitleGroup>
              <ControlBtnGroup>
                <Button
                  css={IconButton.button}
                  className="icoNone"
                  disableRipple
                  onClick={() => {
                    if (!condition.elmPrjId) {
                      openMessageBar({
                        type: 'error',
                        content: t(
                          'com.msg.프로젝트를 선택해 주세요.',
                          '프로젝트를 선택해 주세요.'
                        ),
                      });
                      return;
                    }
                    setOpenScheduleDelayStatusPopup(true);
                  }}
                >
                  {t('setup.label.일정지연보기', '일정지연보기')}
                </Button>
                <Button
                  css={IconButton.button}
                  className="Exceldown"
                  onClick={() => btnExcelExport('RECORD')}
                  disableRipple
                >
                  {t('com.button.다운로드', '다운로드')}
                </Button>
              </ControlBtnGroup>
            </SubTitleLayout>
            <CustomGrid
              id="track-record-grid"
              initialized={(grid) => onInitialized(grid, 'RECORD')}
              layoutDefinition={trackRecordLayout}
              rowData={filteredTrackRecordList}
              align={'center'}
              isSelector={false}
              isReadOnly={true}
              height={190}
            />
          </div>
        </SummaryItem>
        <SummaryItem>
          <div className="gridHeight">
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('setup.label.이슈 사항', '이슈 사항')}</h3>
                <span className="total">
                  {t('com.label.총', '총')}
                  <span>{(delayIssuList || []).length.toLocaleString()}</span>
                  {t('com.label.건', '건')}
                </span>
                {authority?.length > 0 && (
                  <div className="ml-2">
                    <SettingButton onClick={() => setOpenIssuePopup(true)} />
                  </div>
                )}
              </SubTitleGroup>
              <ControlBtnGroup>
                <Button
                  css={IconButton.button}
                  className="Exceldown"
                  onClick={() => btnExcelExport('ISSUE')}
                  disableRipple
                >
                  {t('com.button.다운로드', '다운로드')}
                </Button>
              </ControlBtnGroup>
            </SubTitleLayout>
            <CustomGrid
              id="delay-issue-grid"
              layoutDefinition={delayIssuLayout}
              rowData={delayIssuList}
              align={'center'}
              isSelector={false}
              isReadOnly={true}
              height={190}
              initialized={(grid: any) => {
                onInitialized(grid, 'ISSUE');
                grid.formatItem.addHandler((s: any, e: any) => {
                  const row = e.panel.rows[e.row];
                  const item = row.dataItem;
                  const drawList: string[] = []; // 배경색 처리
                  if (drawList.includes(item?.isuTpCd)) {
                    addClass(e.cell, 'SummaryDly');
                  }
                });
              }}
            />
          </div>
        </SummaryItem>
      </SummaryContainer>

      {openIssuePopup && (
        <CustomDialog
          title={t('setup.label.지연 및 이슈 목록', '지연 및 이슈 목록')}
          size={'lg'}
          open={openIssuePopup}
          onClose={() => setOpenIssuePopup(false)}
        >
          <CustomGrid
            id="delay-issue-grid-popup"
            ref={issuePopupGridRef}
            layoutDefinition={delayIssuPopupLayout}
            rowData={delayIssuPopupList}
            align={'center'}
            isSelector={false}
            isReadOnly={true}
            height={400}
            initialized={(grid) => {
              grid.hostElement.addEventListener('click', (e) => {
                const len = grid.rows.length;
                if (len == 0) return;

                const ht = grid.hitTest(e);
                if (ht.row < 0 || ht.col < 0) return;
                if (ht.panel.cellType != 1) return;

                const item = grid.rows[ht.row].dataItem;
                // 관심항목 등록 및 삭제 추가
                if (e.target instanceof HTMLButtonElement) {
                  const issuCondition = {
                    ...item,
                    bmkDispYn: item.bmkDispYn === 'Y' ? 'N' : 'Y',
                  };
                  switch (e.target.id) {
                    case 'bmkBtn':
                      updateSetupPrjSummDelayIssue(issuCondition).then((res) => {
                        if (res.successOrNot === 'Y') {
                          item.bmkDispYn = item.bmkDispYn === 'Y' ? 'N' : 'Y';
                          grid.refresh();
                        } else {
                          openMessageBar({
                            type: 'error',
                            content: t(
                              'setup.label.저장 중 오류가 발생했습니다.',
                              '저장 중 오류가 발생했습니다.'
                            ),
                          });
                        }
                      });
                      break;
                  }
                }
              });
            }}
          />
        </CustomDialog>
      )}
      {isOpenScheduleChart && (
        <SetupEquipmentScheduleChartPopup
          open={isOpenScheduleChart}
          close={() => setOpenScheduleChart(false)}
          condition={{ ...scheduleChartCondition }}
        />
      )}
      {openIssueDetailPopup && (
        <ProjectIssueCreatePage
          open={openIssueDetailPopup}
          close={() => {
            setOpenIssueDetailPopup(false);
          }}
          mode={PageMode.READ}
          isuId={registerParam?.isuId || ''}
        />
      )}
      {isOpenScheduleDelayStatusPopup && (
        <SetupScheduleDelayStatusPopup
          open={isOpenScheduleDelayStatusPopup}
          close={() => setOpenScheduleDelayStatusPopup(false)}
          condition={{ elmPrjId: condition.elmPrjId, elmPrjNm: condition.elmPrjNm }}
        />
      )}
    </>
  );
};

const SettingButton = (props) => {
  return <Button css={st.btn} {...props}></Button>;
};

const st = {
  btn: css`
    min-width: 24px;
    padding: 0 !important;
    &:before {
      content: '';
      clear: both;
      display: inline-block;
      width: 24px;
      height: 24px;
      background: ${CommonText.Light};
      -webkit-mask: ${SVGIcon.IcoSetting};
      mask-image: ${SVGIcon.IcoSetting};
      mask-size: 24px;
    }
  `,
};

//Contents Tab
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function CustomTabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      className="tabpanel-area"
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default SetupProjectSummaryPage;
